import { readEntry, writeEntry } from './local/db';
import { callEdgeFunction } from './edge';
import { isOnline } from './local/status';

/**
 * Licença de uso: prazo assinado pelo servidor.
 *
 * O app não decide se pode rodar. Ele guarda um "vale" que o servidor assinou
 * e confere a assinatura a cada abertura. A chave aqui é só a pública — dá
 * para verificar, não para assinar. É isso que impede alguém de estender o
 * próprio prazo editando o armazenamento local: sem a chave privada, qualquer
 * alteração quebra a assinatura.
 *
 * Um carimbo de data simples não serviria: bastaria atrasar o relógio do
 * Windows ou editar o IndexedDB.
 */

// Pública de propósito. Ela vai no pacote e pode ser lida — não há nada a
// esconder aqui, porque verificar não é o mesmo que assinar.
//
// Sai de variável de ambiente para que (a) o teste use um par próprio, sem a
// chave privada de produção precisar existir no repositório, e (b) rotacionar
// a chave não exija mexer no código.
const CHAVE_PUBLICA_PADRAO =
  'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE29ssh0SHJKzUqIrKEkvnj5NI8fg3t71Tc3D175kE0iibEmVoi6M2t91VrSUxx6UcWC1jnuGFp6Ii3Cjg6ghyrA==';

const CHAVE_PUBLICA =
  process.env.REACT_APP_LICENCA_CHAVE_PUBLICA || CHAVE_PUBLICA_PADRAO;

const CHAVE_LICENCA = 'licenca';
const CHAVE_RELOGIO = 'licenca:maior-instante';

// Folga para relógio ligeiramente atrasado (fuso, sincronização), sem abrir
// espaço para o truque de voltar dias.
const TOLERANCIA_RELOGIO_MS = 10 * 60 * 1000;

const deBase64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

let chaveImportada = null;
const carregarChavePublica = async () => {
  if (!chaveImportada) {
    chaveImportada = await crypto.subtle.importKey(
      'spki', deBase64(CHAVE_PUBLICA),
      { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify'],
    );
  }
  return chaveImportada;
};

/**
 * Detecta relógio atrasado.
 *
 * Guarda o maior instante já visto. Se o relógio "voltar" além da tolerância,
 * alguém mexeu — e sem isso a expiração de 7 dias seria contornável mudando a
 * data do Windows.
 */
const relogioFoiAtrasado = async () => {
  const agora = Date.now();
  const registro = await readEntry(CHAVE_RELOGIO);
  const maior = registro?.data || 0;

  if (agora + TOLERANCIA_RELOGIO_MS < maior) return true;
  if (agora > maior) await writeEntry(CHAVE_RELOGIO, agora);
  return false;
};

/** Confere assinatura e prazo do vale guardado. */
export async function verificarLicenca() {
  if (await relogioFoiAtrasado()) {
    return { valida: false, motivo: 'relogio_alterado' };
  }

  const registro = await readEntry(CHAVE_LICENCA);
  if (!registro?.data) return { valida: false, motivo: 'sem_licenca' };

  const { licenca, assinatura } = registro.data;
  if (!licenca || !assinatura) return { valida: false, motivo: 'sem_licenca' };

  let confere = false;
  try {
    confere = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      await carregarChavePublica(),
      deBase64(assinatura),
      new TextEncoder().encode(licenca),
    );
  } catch {
    confere = false;
  }

  if (!confere) return { valida: false, motivo: 'assinatura_invalida' };

  const dados = JSON.parse(licenca);
  if (Date.now() > dados.vale_ate) {
    return { valida: false, motivo: 'vencida', dados };
  }

  return { valida: true, dados, expiraEm: dados.vale_ate };
}

/**
 * Pede um vale novo ao servidor. Só faz sentido online.
 * Devolve o motivo da recusa quando a assinatura não está em dia.
 */
export async function renovarLicenca(laboratoryId) {
  if (!isOnline()) return { renovada: false, motivo: 'sem_conexao' };

  try {
    const { ok, status, data } = await callEdgeFunction(
      'emitir-licenca', { laboratory_id: laboratoryId }, { requireSession: true },
    );

    if (!ok || !data?.success) {
      // 402 = assinatura inativa ou vencida. É recusa de negócio, não falha
      // técnica, e a tela precisa dizer isso com todas as letras.
      return {
        renovada: false,
        motivo: status === 402 ? (data?.motivo || 'assinatura_inativa') : 'falha',
        detalhe: data?.error,
      };
    }

    await writeEntry(CHAVE_LICENCA, { licenca: data.licenca, assinatura: data.assinatura });
    await writeEntry(CHAVE_RELOGIO, Date.now());
    return { renovada: true };
  } catch (err) {
    return { renovada: false, motivo: 'sem_conexao', detalhe: err?.message };
  }
}

/**
 * Renova quando dá, e devolve o veredito.
 *
 * A ordem importa: tenta renovar primeiro para que quem está em dia e online
 * nunca veja bloqueio; só depois confere o que está guardado. Offline, o vale
 * antigo segura o app até vencer.
 */
export async function garantirLicenca(laboratoryId) {
  if (laboratoryId && isOnline()) await renovarLicenca(laboratoryId);
  return verificarLicenca();
}

export const MOTIVOS = {
  sem_licenca: 'Este computador ainda não foi liberado. Conecte-se à internet para ativar.',
  vencida: 'A liberação deste computador venceu. Conecte-se à internet para renovar.',
  assinatura_invalida: 'A liberação deste computador não é válida. Conecte-se à internet para renovar.',
  relogio_alterado: 'A data do computador foi alterada. Acerte o relógio e conecte-se à internet.',
  inativa: 'A assinatura deste laboratório está inativa. Fale com o suporte.',
  vencida_assinatura: 'A assinatura deste laboratório venceu. Fale com o suporte.',
  sem_assinatura: 'Este laboratório não tem assinatura registrada. Fale com o suporte.',
};
